import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DatePipe, formatDate } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Button, Modal, SegmentedFilterControl } from '@spirit-testing/component-library';
import { catchError, finalize, map, of, startWith, switchMap } from 'rxjs';
import { ResPillComponent, ResPillStatus } from '../../../components/res-pill/res-pill.component';
import { ResultStatus } from '../../../models/test-run.model';
import {
  ProtocolAnhang,
  ProtocolKommentar,
  ProtocolOptions,
  ProtocolTarget,
  TestProtocol,
} from '../../../models/test-protocol.model';
import { TestRunService } from '../../../services/test-run.service';
import { formatDurationMs } from '../../../shared/duration.util';
import { saveBlob } from '../../../shared/file-download';
import { formatFileSize } from '../../../shared/file-size';

type SectionKey = 'testdaten' | 'kommentare' | 'anhaenge';

interface PreviewState {
  loading: boolean;
  failed: boolean;
  data: TestProtocol | null;
  caseResultId: string | null;
}

interface PreviewRequest {
  target: ProtocolTarget | null;
  options: ProtocolOptions;
  attempt: number;
}

const SECTIONS: SectionKey[] = ['testdaten', 'kommentare', 'anhaenge'];
const IDLE: PreviewState = { loading: false, failed: false, data: null, caseResultId: null };
const PILL: Record<ResultStatus, ResPillStatus> = {
  PASSED: 'passed',
  FAILED: 'failed',
  BLOCKED: 'blocked',
  SKIPPED: 'skipped',
  RUNNING: 'running',
  NOT_RUN: 'not-run',
};
const OPEN_RESULTS = new Set<ResultStatus>(['NOT_RUN', 'RUNNING']);
const DEVIATIONS = new Set<ResultStatus>(['FAILED', 'BLOCKED']);
const UNSAFE_FILENAME_CHARS = /[^A-Za-z0-9._-]/g;

@Component({
  selector: 'app-testprotokoll-dialog',
  imports: [Button, DatePipe, Modal, ResPillComponent, SegmentedFilterControl, TranslatePipe],
  templateUrl: './testprotokoll-dialog.component.html',
  styleUrl: './testprotokoll-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestprotokollDialogComponent {
  private readonly testRunService = inject(TestRunService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly runId = input.required<string>();
  readonly runKey = input.required<string>();
  readonly executionId = input.required<string>();
  readonly executionStatus = input.required<ResultStatus>();
  readonly caseResultId = input<string | null>(null);
  readonly visible = model(false);

  protected readonly sections = SECTIONS;
  protected readonly options = signal<ProtocolOptions>({
    testdaten: true,
    kommentare: true,
    anhaenge: true,
    nurAbweichungen: false,
  });
  protected readonly preview = signal<PreviewState>(IDLE);
  protected readonly downloading = signal(false);
  protected readonly downloadFailed = signal(false);
  protected readonly downloaded = signal(false);
  private readonly attempt = signal(0);

  protected readonly data = computed(() => this.preview().data);
  /** Ein Fall steht schon nach dem ersten fehlgeschlagenen Schritt auf FAILED; erst die Runde sagt, ob er fertig ist. */
  protected readonly closed = computed(() => {
    const protocol = this.data();
    return !!protocol && !OPEN_RESULTS.has(protocol.ergebnis.status) && !OPEN_RESULTS.has(this.executionStatus());
  });
  protected readonly subtitleKey = computed(() =>
    this.closed() ? 'testResults.protocol.subtitle' : 'testResults.protocol.subtitleOpen',
  );
  protected readonly canDownload = computed(() => {
    const { loading, failed, data } = this.preview();
    return !loading && !failed && data !== null && !this.downloading();
  });
  protected readonly stand = computed(() => {
    this.data();
    return new Date();
  });
  protected readonly filename = computed(() => {
    const lauf = this.runKey().replace(UNSAFE_FILENAME_CHARS, '_');
    return `Testprotokoll_${lauf}_${formatDate(this.stand(), 'yyyy-MM-dd', 'en')}.pdf`;
  });
  protected readonly stepCount = computed(() => this.data()?.ergebnis.gesamt ?? 0);
  /** Dasselbe wie der Filter im Backend: nur fehlgeschlagene und blockierte Schritte. */
  protected readonly deviationCount = computed(() => {
    const ergebnis = this.data()?.ergebnis;
    return ergebnis ? ergebnis.fehlgeschlagen + ergebnis.blockiert : 0;
  });
  protected readonly steps = computed(() =>
    (this.data()?.schritte ?? []).map((schritt, index, all) => ({
      ...schritt,
      newGroup: !!schritt.gruppe && schritt.gruppe !== all[index - 1]?.gruppe,
      deviation: DEVIATIONS.has(schritt.status),
    })),
  );
  protected readonly noStepsKey = computed(() => {
    const ergebnis = this.data()?.ergebnis;
    if (!this.options().nurAbweichungen || !ergebnis || ergebnis.gesamt === 0) return 'noSteps';
    return ergebnis.bestanden === ergebnis.gesamt ? 'noDeviations' : 'noFailed';
  });

  private readonly request = computed<PreviewRequest>(() => {
    const caseResultId = this.caseResultId();
    return {
      target:
        this.visible() && caseResultId
          ? {
              projectId: this.projectId(),
              runId: this.runId(),
              executionId: this.executionId(),
              caseResultId,
            }
          : null,
      options: this.options(),
      attempt: this.attempt(),
    };
  });

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.downloaded.set(false);
        this.downloadFailed.set(false);
      }
    });

    toObservable(this.request)
      .pipe(
        switchMap(({ target, options }) => {
          if (!target) return of(this.preview());
          const { caseResultId } = target;
          const previous = this.preview().caseResultId === caseResultId ? this.preview().data : null;
          return this.testRunService.getProtocolPreview(target, options).pipe(
            map((data): PreviewState => ({ loading: false, failed: false, data, caseResultId })),
            catchError(() => of<PreviewState>({ loading: false, failed: true, data: previous, caseResultId })),
            startWith<PreviewState>({ loading: true, failed: false, data: previous, caseResultId }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => this.preview.set(state));
  }

  protected count(key: SectionKey): number | null {
    return this.data()?.verfuegbar[key] ?? null;
  }

  protected isAvailable(key: SectionKey): boolean {
    return !!this.count(key);
  }

  protected isOn(key: SectionKey): boolean {
    return this.options()[key] && this.isAvailable(key);
  }

  protected toggle(key: SectionKey): void {
    this.options.update((options) => ({ ...options, [key]: !options[key] }));
  }

  protected showOnlyDeviations(value: boolean): void {
    this.options.update((options) => ({ ...options, nurAbweichungen: value }));
  }

  protected retry(): void {
    this.attempt.update((attempt) => attempt + 1);
  }

  protected close(): void {
    this.visible.set(false);
  }

  protected download(): void {
    const caseResultId = this.caseResultId();
    if (!this.canDownload() || !caseResultId) return;
    this.downloading.set(true);
    this.downloadFailed.set(false);
    this.downloaded.set(false);
    const target: ProtocolTarget = {
      projectId: this.projectId(),
      runId: this.runId(),
      executionId: this.executionId(),
      caseResultId,
    };
    this.testRunService
      .downloadProtocol(target, {
        testdaten: this.isOn('testdaten'),
        kommentare: this.isOn('kommentare'),
        anhaenge: this.isOn('anhaenge'),
        nurAbweichungen: this.options().nurAbweichungen,
      })
      .pipe(
        finalize(() => this.downloading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ blob, filename }) => {
          saveBlob(blob, filename);
          this.downloaded.set(true);
        },
        error: () => this.downloadFailed.set(true),
      });
  }

  protected testfall(protocol: TestProtocol): string {
    const { testfallKey, titel } = protocol.kopf;
    return testfallKey ? `${testfallKey} · ${titel}` : titel;
  }

  protected ausfuehrung(protocol: TestProtocol): string {
    const { laufKey, ausfuehrungNr } = protocol.kopf;
    return ausfuehrungNr ? `${laufKey} · #${ausfuehrungNr}` : laufKey;
  }

  protected artUndQuelle(protocol: TestProtocol): string {
    const { art, quelle } = protocol.kopf;
    const kind = this.translate.instant(`testResults.protocol.kind.${art}`) as string;
    return quelle ? `${kind} · ${this.translate.instant(`testResults.protocol.source.${quelle}`)}` : kind;
  }

  protected duration(ms: number | undefined): string {
    return formatDurationMs(ms);
  }

  protected pill(status: ResultStatus): ResPillStatus {
    return PILL[status];
  }

  protected statusLabel(status: ResultStatus): string {
    return `testResults.protocol.status.${status}`;
  }

  protected attachmentLine(anhang: ProtocolAnhang, index: number): string {
    const size = formatFileSize(anhang.groesse, this.translate.currentLang || 'de');
    return `A${index + 1} · ${anhang.name} · ${anhang.typ} · ${size}`;
  }

  protected commentAuthor(kommentar: ProtocolKommentar): string {
    const parts = [kommentar.autor?.trim() || '—'];
    if (kommentar.uebergabeAn) {
      parts.push(this.translate.instant('testResults.protocol.doc.handover', { name: kommentar.uebergabeAn }));
    }
    if (kommentar.schrittNr) {
      parts.push(this.translate.instant('testResults.protocol.doc.atStep', { nr: kommentar.schrittNr }));
    }
    return parts.join(' ');
  }
}
